'use client';

import React from 'react';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useEventQuery } from '@/graphql';
import { isValidImageSrc } from '@/lib/utils';
import racesafelogo from '@/public/assets/images/racesafe-logo.png';
import { SafeHtml } from '@/ui/safe-html';

import LoadingAnimation from '../lottie-loading';

interface EventDescriptionProps {
  user: string;
}

const EventDescription: React.FC<EventDescriptionProps> = ({ user }) => {
  const searchParams = useSearchParams();
  const hk = searchParams.get('hk');

  const {
    data: registrationEventData,
    loading: fetchingRegistrationEvent,
    error: registrationEventError,
  } = useEventQuery({
    variables:
      'team' === user ? { volunteer_hash: hk } : { participant_hash: hk },
    skip: !hk,
  });

  const event = registrationEventData?.fetchEvent;

  if (fetchingRegistrationEvent) {
    return <LoadingAnimation />;
  }

  if (event) {
    return (
      <div className="flex flex-col rounded-lg md:max-w-148">
        <div className="flex flex-col items-center justify-center gap-10 p-5 text-center md:p-5">
          <h2 className="font-primary text-3xl text-center text-white">
            {event.name}
          </h2>
          {isValidImageSrc(event.logo) && (
            <div className="flex w-full flex-row items-center justify-around">
              <Image
                alt="Racesafe logo"
                src={event.logo}
                width={200}
                height={200}
              />
            </div>
          )}

          <SafeHtml
            className="text-center text-white/90 [&_a]:text-white"
            html={
              'team' === user
                ? event.team_description
                : event.participant_description
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-10">
      <h3 className="font-primary text-2xl text-center text-white">
        For your health and safety, please share your information with your
        race's medical team.
        <Image
          alt="Racesafe logo"
          height={240}
          src={racesafelogo}
          style={{ display: 'inline', width: 'auto' }}
          width={100}
        />
        ™ is secure, <a href="/privacy">private</a>, and takes less than 5
        minutes to create a profile.
        <Image
          alt="Racesafe logo"
          height={240}
          src={racesafelogo}
          style={{ display: 'inline' }}
          width={100}
        />{' '}
        ™ respects your privacy and is committed to maintaining your
        confidentiality.
      </h3>
    </div>
  );
};

export default EventDescription;
